import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';

export default function AdminExamOrdersPage() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<any>(null);
  const [examCode, setExamCode] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [adminStatus, setAdminStatus] = useState('approved');

  const { data: orders, isLoading } = useQuery({
    queryKey: ['admin-exam-orders'],
    queryFn: async () => {
      const res = await fetch('/api/admin/exam-orders', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch('/api/admin/exam-orders/' + id, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin-exam-orders'] }); },
  });

  const deleteAllMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/admin/exam-orders', { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin-exam-orders'] }); },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, adminStatus, examCode, adminNotes }: any) => {
      const res = await fetch(`/api/admin/exam-orders/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ adminStatus, examCode, adminNotes }),
      });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-exam-orders'] });
      setSelected(null);
      setExamCode('');
      setAdminNotes('');
    },
  });

  const handleUpdate = () => {
    if (!selected) return;
    if (adminStatus === 'approved' && !examCode) {
      alert('Exam code is required for approval');
      return;
    }
    updateMutation.mutate({ id: selected.id, adminStatus, examCode: examCode || undefined, adminNotes: adminNotes || undefined });
  };

  const paymentColor = (s: string) => {
    if (s === 'paid') return { bg: '#dcfce7', color: '#16a34a' };
    if (s === 'pending') return { bg: '#fef9c3', color: '#ca8a04' };
    if (s === 'failed') return { bg: '#fee2e2', color: '#dc2626' };
    return { bg: '#f3f4f6', color: '#6b7280' };
  };

  const adminColor = (s: string) => {
    if (s === 'approved') return { bg: '#dcfce7', color: '#16a34a' };
    if (s === 'pending') return { bg: '#fef9c3', color: '#ca8a04' };
    if (s === 'rejected') return { bg: '#fee2e2', color: '#dc2626' };
    if (s === 'completed') return { bg: '#dbeafe', color: '#2563eb' };
    return { bg: '#f3f4f6', color: '#6b7280' };
  };

  if (isLoading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold">Exam Orders</h1>
        <p className="text-gray-500 mt-2">Manage exam purchases, approve requests, and issue exam codes</p>
      </div>
      {orders?.length > 0 && (
        <button
          onClick={() => { if (window.confirm('Delete ALL exam orders? This cannot be undone.')) deleteAllMutation.mutate(); }}
          className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
          disabled={deleteAllMutation.isPending}
        >
          {deleteAllMutation.isPending ? 'Deleting...' : 'Delete All Orders'}
        </button>
      )}

      {!orders?.length ? (
        <Card><CardContent className="pt-6 text-center"><p className="text-gray-500">No exam orders yet</p></CardContent></Card>
      ) : (
        <div className="grid gap-4">
          {orders.map((order: any) => {
            const meta = order.metadata as any;
            const pc = paymentColor(order.status);
            const ac = adminColor(order.adminStatus || 'pending');
            return (
              <Card key={order.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="font-semibold text-lg">
                          {meta?.firstName || order.userName} {meta?.lastName || ''}
                        </h3>
                        <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: pc.bg, color: pc.color, fontWeight: 600 }}>
                          Payment: {order.status}
                        </span>
                        <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: ac.bg, color: ac.color, fontWeight: 600 }}>
                          Admin: {order.adminStatus || 'pending'}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm text-gray-600 mb-3">
                        <div>
                          <p className="font-semibold text-gray-800">Email</p>
                          <p>{meta?.email || order.userEmail}</p>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">Exam</p>
                          <p>{order.examTitle}</p>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">Type</p>
                          <p>{order.examType === 'computer' ? '💻 Computer-Based' : '📝 Paper-Based'}</p>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">Amount</p>
                          <p className="font-medium">{order.amount?.toLocaleString()} {order.currency}</p>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">Date</p>
                          <p>{order.createdAt ? new Date(order.createdAt).toLocaleDateString() : '-'}</p>
                        </div>
                        {meta?.preferredDate && (
                          <div>
                            <p className="font-semibold text-gray-800">Preferred Date</p>
                            <p>{meta.preferredDate} {meta.preferredTime || ''}</p>
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-gray-800">Account</p>
                          <p>{order.userName} ({order.userEmail})</p>
                        </div>
                      </div>

                      {order.examCode && (
                        <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-lg inline-block">
                          <span className="text-sm font-semibold text-green-800">Exam Code: </span>
                          <span className="font-mono bg-white px-2 py-1 rounded text-sm">{order.examCode}</span>
                        </div>
                      )}

                      {order.adminNotes && (
                        <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm font-semibold text-gray-800">Admin Notes</p>
                          <p className="text-sm text-gray-600">{order.adminNotes}</p>
                        </div>
                      )}
                    </div>

                    <div className="ml-4">
                      {order.status === 'paid' && (order.adminStatus === 'pending' || !order.adminStatus) && (
                        <button
                          onClick={() => { setSelected(order); setAdminStatus('approved'); setExamCode(''); setAdminNotes(''); }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                        >
                          Review
                        </button>
                      )}
                      {order.status === 'paid' && order.adminStatus && order.adminStatus !== 'pending' && (
                        <button
                          onClick={() => { setSelected(order); setAdminStatus(order.adminStatus); setExamCode(order.examCode || ''); setAdminNotes(order.adminNotes || ''); }}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300"
                        >
                          Edit
                        </button>
                      )}
                      <button
                        onClick={() => { if (window.confirm('Delete this exam order?')) deleteMutation.mutate(order.id); }}
                        className="px-3 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200"
                        disabled={deleteMutation.isPending}
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Review Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardContent className="pt-6">
              <h2 className="text-xl font-bold mb-2">Review Exam Order</h2>
              <p className="text-sm text-gray-600 mb-4">
                {(selected.metadata as any)?.firstName} {(selected.metadata as any)?.lastName} — {selected.examTitle}
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Admin Status</label>
                  <select value={adminStatus} onChange={e => setAdminStatus(e.target.value)}
                    className="w-full p-2 border rounded-lg">
                    <option value="approved">Approve</option>
                    <option value="rejected">Reject</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>

                {(adminStatus === 'approved' || adminStatus === 'scheduled' || adminStatus === 'completed') && (
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Exam Code {selected.examType === 'computer' ? '*' : '(Optional)'}
                    </label>
                    <input value={examCode} onChange={e => setExamCode(e.target.value)}
                      placeholder="e.g., EXAM-2024-001"
                      className="w-full p-2 border rounded-lg" />
                    {selected.examType === 'paper' && (
                      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800">
                          <strong>Note:</strong> For paper-based exams, the student will receive an email from PeopleCert once the booking is confirmed.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-1">Admin Notes</label>
                  <textarea value={adminNotes} onChange={e => setAdminNotes(e.target.value)}
                    placeholder="Add any notes..." rows={3}
                    className="w-full p-2 border rounded-lg" />
                </div>

                <div className="flex gap-2 justify-end">
                  <button onClick={() => setSelected(null)}
                    className="px-4 py-2 border rounded-lg text-sm font-medium hover:bg-gray-50">
                    Cancel
                  </button>
                  <button onClick={handleUpdate} disabled={updateMutation.isPending}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                    {updateMutation.isPending ? 'Updating...' : 'Update'}
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
